import { Component } from '@theme/component';

/**
 * Job listings component. Filters the vacancy rows by category entirely on the client: every job is
 * rendered server-side and the pills just toggle row visibility, so there is no reload and no URL
 * change. The dataset is small (a handful of vacancies), which is what makes this cheaper than a
 * section re-render.
 *
 * Department headings are hidden while a category filter is active — the visible set is then a
 * single category, so the heading only repeats what the active pill already says. A heading whose
 * group has no visible rows would also dangle above the next group.
 *
 * The pills live in the separate `_apc-job-filters` block, so they are found by querying the DOM
 * rather than through a ref: refs are collected per block, and a child block's `ref` does not
 * register on this component.
 *
 * @extends {Component<{ rows: HTMLElement[], headings: HTMLElement[], empty: HTMLElement }>}
 */
export default class ApcJobListings extends Component {
  /** @type {string} */
  #active = 'all';

  /**
   * Filters rows to the clicked pill's category.
   *
   * @param {Event} event - The click event from a filter pill.
   */
  filter(event) {
    const pill = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-category]') : null
    );
    if (!pill) return;

    const category = pill.dataset.category;
    if (!category || category === this.#active) return;

    this.#active = category;
    this.#applyFilter();
  }

  #applyFilter() {
    const rows = this.#toArray(this.refs.rows);
    const headings = this.#toArray(this.refs.headings);
    const showAll = this.#active === 'all';

    let visibleCount = 0;

    for (const row of rows) {
      const matches = showAll || row.dataset.category === this.#active;
      row.hidden = !matches;
      if (matches) visibleCount += 1;
    }

    // Headings only make sense in the unfiltered view.
    for (const heading of headings) {
      heading.hidden = !showAll;
    }

    for (const pill of this.#pills()) {
      const isActive = pill.dataset.category === this.#active;
      pill.classList.toggle('apc-job-filters__pill--active', isActive);
      pill.setAttribute('aria-pressed', String(isActive));
    }

    const { empty } = this.refs;
    if (empty instanceof HTMLElement) {
      empty.hidden = visibleCount > 0;
    }
  }

  /**
   * The pills, queried from the DOM because they belong to the `_apc-job-filters` child block and
   * so are outside this component's refs. Scoped to the pill class rather than `[data-category]`,
   * which the rows and headings also carry.
   *
   * @returns {HTMLElement[]}
   */
  #pills() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.apc-job-filters__pill')));
  }

  /**
   * Refs are a single element when only one node carries the `ref` attribute, and an array
   * otherwise — normalise so a one-vacancy list behaves like any other.
   *
   * @param {Element | Element[] | undefined} ref
   * @returns {HTMLElement[]}
   */
  #toArray(ref) {
    if (!ref) return [];
    const list = Array.isArray(ref) ? ref : [ref];
    return /** @type {HTMLElement[]} */ (list.filter((node) => node instanceof HTMLElement));
  }
}

if (!customElements.get('apc-job-listings')) {
  customElements.define('apc-job-listings', ApcJobListings);
}
